import React, { useState, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { ThemeProvider, Snackbar, Alert, Typography, useTheme } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import theme from "./utils/theme";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { getConfig } from "./config";
import { ApolloProvider, useQuery } from "@apollo/client";
import { createApolloClient } from "./apis/apolloClient";
import DashboardSkeleton from "./components/Skeletons/DashboardSkeleton";
import MaintenanceGate from "./components/Maintenance/MaintenanceGate";
import { GET_USER_DETAILS } from "./apis/dashboard-apis/dashboardDetailApi";
import { GET_FRAMEWORK_LIST } from "./apis/settingsApis/framework-apis/frameworkApis";
import { Provider } from "react-redux";
import { store } from "./store";
import useAppStore from "./stores";

const onRedirectCallback = (appState) => {
  window.history.replaceState(
    {},
    document.title,
    appState?.returnTo || window.location.pathname
  );
};

const config = getConfig();
const providerConfig = {
  domain: config.domain,
  clientId: config.clientId,
  onRedirectCallback,
  authorizationParams: {
    redirect_uri: window.location.origin,
    ...(config.audience ? { audience: config.audience } : null),
  },
};

// Component to handle client setup with Auth0 tokens
const ApolloSetup = () => {
  const {
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
  } = useAuth0();
  const [client, setClient] = React.useState(null);

  React.useEffect(() => {
    const setupApolloClient = async () => {
      try {
        if (isAuthenticated) {
          const accessToken = await getAccessTokenSilently();
          const idToken = (await getIdTokenClaims()).__raw;
          const apolloClient = createApolloClient(accessToken, idToken);
          setClient(apolloClient);
        } else if (!isLoading) {
          loginWithRedirect({
            appState: { returnTo: window.location.pathname },
          });
        }
      } catch (error) {
        console.error("Error setting up Apollo client:", error);
      }
    };

    setupApolloClient();
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
  ]);

  if (isLoading || !isAuthenticated || !client) {
    return <DashboardSkeleton />;
  }

  return (
    <ApolloProvider client={client}>
      <MaintenanceGate>
        <ApolloDataWrapper />
      </MaintenanceGate>
    </ApolloProvider>
  );
};

const ApolloDataWrapper = () => {
  const theme = useTheme();
  const { logout } = useAuth0();
  const { data, loading, error } = useQuery(GET_USER_DETAILS, {
    fetchPolicy: "network-only",
  });
  const {
    data: frameworkData,
    loading: frameworkLoading,
    error: frameworkError,
  } = useQuery(GET_FRAMEWORK_LIST, {
    fetchPolicy: "network-only",
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const setEntitlements = useAppStore((state) => state.setEntitlements);
  const entitlementsHydrated = useAppStore(
    (state) => state.entitlementsHydrated
  );

  useLayoutEffect(() => {
    if (loading || error) return;
    const companyDetails = data?.getAccountDetails?.userCompanyDetails;
    const userDetails = data?.getAccountDetails?.userDetails;
    setEntitlements(companyDetails?.plan, userDetails?.entitlements || {});
  }, [data, loading, error, setEntitlements]);

  useEffect(() => {
    if (error || frameworkError) {
      setShowSnackbar(true);
      setTimeout(() => {
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }, 2000);
    }
  }, [error, frameworkError, logout]);

  if (error || frameworkError) {
    return (
      <>
        <DashboardSkeleton />
        <Snackbar
          open={showSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            top: "24px !important",
          }}
        >
          <Alert
            severity="error"
            icon={<InfoIcon sx={{ fontSize: 20 }} />}
            sx={{
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${theme.customColors?.background?.error || "#FEF2F2"} 0%, ${theme.customColors?.background?.error || "#FEF2F2"} 100%)`,
              border: `1px solid ${theme.customColors?.status?.error?.main || "#EF4444"}25`,
              boxShadow: "0 2px 8px rgba(239, 68, 68, 0.08), 0 1px 3px rgba(239, 68, 68, 0.12)",
              py: 2,
              px: 3.5,
              minWidth: "500px",
              maxWidth: "700px",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.12), 0 2px 4px rgba(239, 68, 68, 0.16)",
              },
              "& .MuiAlert-icon": {
                color: theme.customColors?.status?.error?.main || "#EF4444",
                alignSelf: "center",
                mt: 0,
                mr: 1.5,
                fontSize: "20px",
              },
              "& .MuiAlert-message": {
                color: theme.customColors?.text?.primary || "#111827",
                width: "100%",
                padding: 0,
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9375rem",
                fontWeight: 500,
                color: theme.customColors?.text?.primary || "#111827",
                lineHeight: 1.7,
                whiteSpace: "nowrap",
              }}
            >
              It looks like you don't have access to this application. Please contact support.
            </Typography>
          </Alert>
        </Snackbar>
      </>
    );
  }

  if (loading || frameworkLoading || !entitlementsHydrated) {
    return <DashboardSkeleton />;
  }

  return <App data={data} />;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <Auth0Provider {...providerConfig}>
        <ThemeProvider theme={theme}>
          <ApolloSetup />
        </ThemeProvider>
      </Auth0Provider>
    </Provider>
  </React.StrictMode>
);

reportWebVitals();
